import React, { useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import Paper from '@mui/material/Paper';
import { connect, useDispatch } from 'react-redux';
import { dealerListingRequest, dealerListingUpdateUserStatus, dealerPhone ,verifiedUserEmail} from '../../../redux/actionDispatcher/admin/dealerListingDispatcher';
import { ApiURLs } from '../../../constant/ApiUrls';
import Swal from 'sweetalert2';
import { patchData } from '../../../services/api';
import { NavigationRoutes } from '../../../constant/Routes';
import { useNavigate } from 'react-router-dom';
import moment from "moment";

import { CheckCircle, XCircle, Flag } from "lucide-react";
import AddDealer from './AddDealer';
// Dealerships under one account: a group's forecourts, each with its own
// address and phone. Self-contained, renders its own button and modal.
import DealerBranches from './DealerBranches';
import DealerDataPanel from './DealerDataPanel';
import { postData, fetchData } from '../../../services/api';
import { confirmDeleteOrMerge } from '../../../services/deleteOrMerge';
// import AddTierPopup from './AddTierToDealer';

import { getTierListing } from '../../../features/tiers/redux/tierDispatcher';

function DealerLisitingTable({ dealerListing }) {


  const dispatch = useDispatch();
  const navigate = useNavigate();




  const [tiers, setTiers] = useState([]);
  const fetchedRef = React.useRef(false);

  /* Coming back to the same dealer (client, 16/10/2026: "when we are updating
   * the dealers, the page jumps to a different place when we go back").
   *
   * The page and sort were already remembered, but not WHERE on the page: the
   * table scrolls inside this Paper and inside the grid's own scroller, both of
   * which start at the top on return - and then the summary cards above load
   * and push everything down again. So the dealer that was opened, and both
   * scroll positions, are noted when View is pressed, and put back once the
   * rows are here. The positions are re-applied for a moment while the cards
   * finish loading, unless the person starts scrolling themselves, and the
   * dealer's row is lit up briefly so the eye lands on it. */
  const RETURN_KEY = 'superadmin.dealers.return';
  const paperRef = React.useRef(null);
  const [returnId, setReturnId] = useState(null);
  const gridScroller = () => paperRef.current && paperRef.current.querySelector('.MuiDataGrid-virtualScroller');
  const rememberReturn = (id) => {
    try {
      sessionStorage.setItem(RETURN_KEY, JSON.stringify({
        id,
        paper: paperRef.current ? paperRef.current.scrollTop : 0,
        grid: gridScroller() ? gridScroller().scrollTop : 0,
        win: window.scrollY,
        // where the dealer's row sat on the screen - the thing to give back
        rowTop: (() => {
          const r = paperRef.current && paperRef.current.querySelector(`.MuiDataGrid-row[data-id="${id}"]`);
          return r ? r.getBoundingClientRect().top : null;
        })(),
      }));
    } catch (e) { /* no storage, no memory - nothing breaks */ }
  };

  /* Where you were in this table, kept across leaving and coming back.
   *
   * All of this used to be plain useState, so it reset the moment the component
   * remounted. Open a dealer, press the browser Back button, and you were
   * returned to page 1 sorted newest-first - which lands on whichever dealer was
   * added last, not the one you were working through. Going through 114 accounts
   * one at a time that is maddening, and it is exactly how it was reported.
   *
   * Held in sessionStorage rather than the URL because it has to survive a plain
   * Back, and it should not outlive the browser session - coming back tomorrow
   * should start at the top. Reading is wrapped because private-mode browsers
   * throw on sessionStorage, and a table that will not render is far worse than
   * a table that forgot its page. */
  const VIEW_KEY = 'superadmin.dealers.view';
  const savedView = (() => {
    try {
      return JSON.parse(sessionStorage.getItem(VIEW_KEY) || '{}') || {};
    } catch (e) {
      return {};
    }
  })();

  // ✅ State for pagination
  const [page, setPage] = useState(Number.isInteger(savedView.page) ? savedView.page : 0);
  const [pageSize, setPageSize] = useState(
    [10, 25, 50].includes(savedView.pageSize) ? savedView.pageSize : 10
  );
  const [sortModel, setSortModel] = useState(
    Array.isArray(savedView.sortModel) && savedView.sortModel.length
      ? savedView.sortModel
      : [{ field: "created_at", sort: "desc" }]
  );
  const [searchTerm, setSearchTerm] = useState(
    typeof savedView.searchTerm === 'string' ? savedView.searchTerm : ''
  );
  // '' means every sector. Picking one narrows the table to it -- car dealers, estate
  // agents, recruiters and agri services are different businesses and were never meant
  // to share one undifferentiated list.
  const [sector, setSector] = useState(
    typeof savedView.sector === 'string' ? savedView.sector : ''
  );
  // Bumped after a verify / approve / reject so the summary cards recount themselves.
  const [dataVersion, setDataVersion] = useState(0);
  // Dealers who take "Reserve for €99" on listit.ie (21/10/2026).
  const [rsvIds, setRsvIds] = useState(new Set());
  useEffect(() => {
    fetchData(`${ApiURLs.DEALER_VERIFY.replace(/dealer$/, '')}reservation-dealers`)
      .then((d) => setRsvIds(new Set(((d && d.data && d.data.ids) || []).map(Number))))
      .catch(() => {});
  }, []);
  const handleReservations = (row, next) => {
    const who = row.business_name || row.name || `dealer ${row.id}`;
    Swal.fire({
      title: next ? 'Switch on Reserve for €99?' : 'Switch off Reserve for €99?',
      html: `<div style="text-align:left;font-size:14px;line-height:1.6"><b>${who}</b></div>` +
        `<p style="margin-top:12px;font-size:13px;color:#667085">${next
          ? 'Buyers will see a "Reserve for €99" button on this dealer\'s cars. The €99 is only authorised on the buyer\'s card and released after 48 hours.'
          : 'The button disappears from this dealer\'s cars. Reservations already made run their 48 hours.'}</p>`,
      showCancelButton: true, cancelButtonText: 'Cancel',
      confirmButtonText: next ? 'Yes, switch on' : 'Yes, switch off',
      confirmButtonColor: next ? '#0f766e' : '#eb5757', cancelButtonColor: 'gray',
      reverseButtons: true, focusCancel: true, width: 480,
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      const d = await postData({ enabled: next }, `${ApiURLs.DEALER_VERIFY}/${row.id}/reservations`);
      if (d && d.status === 1) setRsvIds((prev) => { const s2 = new Set(prev); if (d.data.enabled) s2.add(Number(row.id)); else s2.delete(Number(row.id)); return s2; });
    });
  };




  const handleVerifyEmail = (row) => {
    // console.log("Deleting User ID:", row);
    Swal.fire({
      // title: `${"Verify Phone Confirmation"}`,
      html:

        "Are you sure?" +
        "<br>" +
        `You want to verify email <b>${row.email}</b> of <b>${row.name}</b>`,
      showCancelButton: true,
      cancelButtonText: "Cancel",
      confirmButtonText: `Yes, Verify`,
      showCloseButton: true,
      cancelButtonColor: "gray",
      confirmButtonColor: "#4f83df",
      reverseButtons: true
    }).then(async (result) => {
      if (result.isConfirmed) {
        dispatch(verifiedUserEmail({ userId: row.id, email_verified: 1 }));
        var response = await patchData(`${ApiURLs.USER_UPDATE}/${row.id}`, { email_verified: 1 });
        if (response.status !== 1) {

          dispatch(verifiedUserEmail({ userId: row.id, email_verified: 1 }));
        }
      }
    });

    // Add your delete logic here
  };


  // Refetch the current page of the table. Used after anything that changes a row.
  const reloadDealers = () => {
    const sf = sortModel[0]?.field || "created_at";
    const so = sortModel[0]?.sort || "desc";
    dispatch(dealerListingRequest(sf, so, page + 1, pageSize, searchTerm, sector));
    setDataVersion((v) => v + 1);
  };

  // The manual sign-off. This is the only thing that sets it -- no job, no import, no
  // scraper. It means a person looked at the dealer's details and is happy they are right.
  //
  // It asks first. The chip sits in a dense grid of rows that all look alike, and a
  // single stray click used to change a dealer's status with nothing to undo it and no
  // way of knowing afterwards which row was hit. The dealer's name is in the question
  // for that reason: the mistake to catch is not "did I mean to click" but "did I click
  // the row I thought I did".
  const handleVerify = (row, next) => {
    const who = row.business_name || row.name || `dealer ${row.id}`;
    Swal.fire({
      title: next ? 'Mark this dealer as verified?' : 'Remove the verified mark?',
      html:
        `<div style="text-align:left;font-size:14px;line-height:1.6">` +
        `<b>${who}</b>` +
        (row.email ? `<br><span style="color:#667085">${row.email}</span>` : '') +
        `</div>` +
        (next
          ? `<p style="margin-top:12px;font-size:13px;color:#667085">This records that someone has checked this dealer's details and is happy they are right.</p>`
          : `<p style="margin-top:12px;font-size:13px;color:#667085">This dealer has already been signed off. Removing the mark puts them back on the to-check list.</p>`),
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: next ? 'Yes, mark verified' : 'Yes, remove it',
      confirmButtonColor: next ? '#00a651' : '#eb5757',
      cancelButtonColor: 'gray',
      // Cancel sits where the eye lands first, and is the default, so pressing
      // Enter out of habit does not change anything.
      reverseButtons: true,
      focusCancel: true,
      width: 480,
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      await postData({ verified: next }, `${ApiURLs.DEALER_VERIFY}/${row.id}/verify`);
      reloadDealers();
    });
  };

  // A Google business the enrichment job matched by name but could not confirm by website.
  // On the first run one of these matched a chartered surveyor to an unrelated firm, so
  // nothing here is published until it is approved.
  const handleGoogleSuggestion = (row, approve) => {
    let s = {};
    try { s = JSON.parse(row.google_suggestion || '{}'); } catch (e) { s = {}; }
    Swal.fire({
      title: approve ? 'Approve this Google match?' : 'Reject this Google match?',
      html:
        `<div style="text-align:left;font-size:14px;line-height:1.6">` +
        `<b>Our dealer:</b> ${row.business_name || row.name || '-'}<br>` +
        `<b>Our website:</b> ${s.our_website || '-'}<br><hr>` +
        `<b>Google says:</b> ${s.name || '-'}<br>` +
        `<b>Google website:</b> ${s.website || '-'}<br>` +
        `<b>Rating:</b> ${s.rating ? `${s.rating} (${s.total_reviews} reviews)` : '-'}<br>` +
        `<b>Address:</b> ${s.address || '-'}` +
        `</div>` +
        (approve
          ? `<p style="margin-top:12px;font-size:13px;color:#667085">Approving publishes this rating, reviews and opening hours on the dealer's page.</p>`
          : `<p style="margin-top:12px;font-size:13px;color:#667085">Rejecting discards the match. Nothing is published.</p>`),
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: approve ? 'Yes, approve' : 'Yes, reject',
      confirmButtonColor: approve ? '#00a651' : '#eb5757',
      cancelButtonColor: 'gray',
      reverseButtons: true,
      width: 560,
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      await postData({ approve }, `${ApiURLs.DEALER_GOOGLE_SUGGESTION}/${row.id}/google-suggestion`);
      reloadDealers();
    });
  };

  // Every field we try to hold on a dealer, in the order Chris reads them. Google reviews
  // and opening times lead because they are the two he named.
  const DATA_FIELDS = [
    { key: 'has_google_reviews', short: 'G', label: 'Google reviews' },
    { key: 'opening_hours_count', short: 'H', label: 'Opening times' },
    { key: 'has_phone', short: 'P', label: 'Phone' },
    { key: 'has_website', short: 'W', label: 'Website' },
    { key: 'has_logo', short: 'L', label: 'Logo' },
    { key: 'has_address', short: 'A', label: 'Address' },
    { key: 'has_about', short: 'D', label: 'About' },
  ];

  const dealerListingHeading = [

    {
      // The number the dealer quotes on the phone. Permanent, never reissued.
      field: "account_number", headerName: "Account No", width: 100,
      renderCell: (params) => (
        params.value
          ? <span style={{ fontFamily: "monospace", fontSize: "13px", letterSpacing: "0.5px" }}>{params.value}</span>
          : <span style={{ color: "#999" }}>--</span>
      ),
    },
    {
      field: "created_at", headerName: "Join Date", width: 100,
      // renderCell: (params) => moment(params.value).format("DD/MM/YYYY HH:mm:ss"), 
      renderCell: (params) => {
        const formattedDate = moment(params.value).format("DD/MM/YYYY");
        const fullDateTime = moment(params.value).format("DD/MM/YYYY HH:mm:ss");
        return (
          <span title={fullDateTime}>
            {formattedDate}
          </span>
        );
      },

      // renderCell: (params) => params.api.getRowIndexRelativeToVisibleRows(params.id) + 1,

    },
    {

      field: "business_name", headerName: "Dealer Name", width: 160,
      renderCell: (params) => (
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          {params.row.is_flagged === 1 && (
            <Flag color="red" size={16} fill="red" title="Flagged - Suspicious" />
          )}
          <span>{params.value}</span>
        </div>
      ),
    },
    {
      flex: 2,
      field: "email", headerName: "Email", width: 130,
      //  renderCell: (params) => (
      //   <span style={{ color: params.row.email_verified === 1 ? "green" : "red" }}>
      //     {params.value}
      //   </span>
      // ), 


      renderCell: (params) => {
        const isVerified = params.row.email_verified === 1;



        return (
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            {params.value.trim() !== "Not Added" &&
              (isVerified ? (
                <CheckCircle className="icon-fixed" color="green" size={18} />
              ) : (
                <XCircle className="icon-fixed" color="red" size={18} />
              ))
            }
            <span >{params.value}</span>

          </div>
        );
      },
    },


    // {
    //   field: "email_verified", headerName: "Email Status", width: 100,
    //   valueGetter: (value, row) => `${row.email_verified === 1 ? 'Verified' : 'Not Verified'}`,
    //   renderCell: (params) => (
    //     <span style={{ color: params.row.email_verified === 1 ? "green" : "red" }}>
    //       {params.value}
    //     </span>
    //   ),

    // },


    {

      field: "number",
      headerName: "Number",
      flex: 1,
      minWidth: 130,

      maxWidth: 180,
      sortable: false,
      valueGetter: (value, row) => `${row.flag || ''} ${row.number || 'Not Added'}`,
      // renderCell: (params) => (
      //   <span style={{ color: params.row.otp_verified === 1 ? "green" : "red" }}>

      //     {params.value==="Not Added"?params.value:`+${params.value}`}
      //   </span>
      // ),
      renderCell: (params) => {
        const isVerified = params.row.otp_verified === 1;

        const numberValue =
          params.value.trim() === "Not Added" ? params.value.trim() : `+${params.value}`;

        return (
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            {params.value.trim() !== "Not Added" &&
              (isVerified ? (
                <CheckCircle className="icon-fixed" color="green" size={18} />
              ) : (
                <XCircle className="icon-fixed" color="red" size={18} />
              ))
            }
            <span style={
              params.value.trim() === "Not Added" ? { color: "red" } : {}
            }>{numberValue}</span>

          </div>
        );
      },
    },


    // {

    //   field: "total_hours_logged",
    //   headerName: "LogIn\nHrs.",
    //   flex: 1,
    //   minWidth: 80,

    //   maxWidth: 80,
    //   sortable: true,
    //   valueGetter: (value, row) => `${row.total_hours_logged || '0'} hrs`,
    //   // renderCell: (params) => params.value




    // },

    {

      field: "ads_count",
      headerName: "Ads Live",
      flex: 1,
      minWidth: 50,

      maxWidth: 80,
      sortable: true,
      // valueGetter: (value, row) => `${'0'}`,
      // renderCell: (params) => params.value




    },
    {
      field: "primary_sector",
      headerName: "Sector",
      width: 110,
      sortable: true,
      renderCell: (params) => {
        const s = params.value;
        const colour = { Motors: '#1b87f4', Property: '#00a651', Jobs: '#9b51e0', 'Agri Services': '#f2994a' }[s] || '#98a2b3';
        return (
          <span style={{
            background: `${colour}18`, color: colour, border: `1px solid ${colour}44`,
            borderRadius: 20, padding: '2px 9px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
          }}>
            {s || 'Unassigned'}
          </span>
        );
      },
    },
    {
      field: "profile_data",
      headerName: "Info collected",
      width: 190,
      sortable: false,
      renderCell: (params) => {
        const r = params.row;
        // opening_hours_count is a count, the rest are 0/1 -- both read as "have it".
        const have = (f) => Number(r[f.key] || 0) > 0;
        const got = DATA_FIELDS.filter(have).length;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, height: '100%' }}>
            <span style={{
              fontSize: 11, fontWeight: 700, minWidth: 26,
              color: got === DATA_FIELDS.length ? '#00a651' : got >= 5 ? '#f2994a' : '#eb5757',
            }}>
              {got}/{DATA_FIELDS.length}
            </span>
            <div style={{ display: 'flex', gap: 2 }}>
              {DATA_FIELDS.map((f) => (
                <span
                  key={f.key}
                  title={`${f.label}: ${have(f) ? 'collected' : 'MISSING'}`}
                  style={{
                    width: 16, height: 16, borderRadius: 3, fontSize: 9, fontWeight: 700,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    background: have(f) ? '#e6f6ed' : '#fdecec',
                    color: have(f) ? '#00a651' : '#eb5757',
                    border: `1px solid ${have(f) ? '#b7e4c9' : '#f7c5c5'}`,
                  }}
                >
                  {f.short}
                </span>
              ))}
            </div>
          </div>
        );
      },
    },
    {
      field: "data_verified",
      headerName: "Verified",
      width: 165,
      sortable: true,
      renderCell: (params) => {
        const r = params.row;
        // A pending Google match outranks the tick: there is no point signing a dealer off
        // while the biggest thing on their profile is still a guess.
        if (r.google_suggestion) {
          return (
            <div style={{ display: 'flex', gap: 4, alignItems: 'center', height: '100%' }}>
              <button
                onClick={() => handleGoogleSuggestion(r, true)}
                title="Approve the Google business we matched to this dealer"
                style={{
                  background: '#00a651', color: '#fff', border: 'none', borderRadius: 4,
                  fontSize: 10, padding: '4px 7px', cursor: 'pointer', fontWeight: 700,
                }}
              >
                Approve
              </button>
              <button
                onClick={() => handleGoogleSuggestion(r, false)}
                title="This is not the right business"
                style={{
                  background: '#fff', color: '#eb5757', border: '1px solid #eb5757', borderRadius: 4,
                  fontSize: 10, padding: '4px 7px', cursor: 'pointer', fontWeight: 700,
                }}
              >
                Reject
              </button>
            </div>
          );
        }
        const on = Number(r.data_verified) === 1;
        return (
          <button
            onClick={() => handleVerify(r, !on)}
            title={on ? 'Signed off. Click to un-verify.' : 'Not checked yet. Click once you are happy the details are right.'}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              background: on ? '#e6f6ed' : '#fff',
              color: on ? '#00a651' : '#98a2b3',
              border: `1px solid ${on ? '#b7e4c9' : '#d0d5dd'}`,
              borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer',
            }}
          >
            {on ? <CheckCircle size={13} /> : <XCircle size={13} />}
            {on ? 'Verified' : 'To check'}
          </button>
        );
      },
    },
    {
      field: "reservations", headerName: "Reserve €99", width: 110, sortable: false,
      renderCell: (params) => {
        const on = rsvIds.has(Number(params.row.id));
        return (
          <button
            onClick={() => handleReservations(params.row, !on)}
            title={on ? 'Buyers can reserve this dealer\'s cars. Click to switch off.' : 'Click to let buyers reserve this dealer\'s cars for €99.'}
            style={{
              background: on ? '#e6f6f4' : '#fff', color: on ? '#0f766e' : '#98a2b3',
              border: `1px solid ${on ? '#99d6cf' : '#d0d5dd'}`, borderRadius: 20, padding: '3px 10px',
              fontSize: 11, fontWeight: 700, cursor: 'pointer',
            }}
          >
            {on ? 'On' : 'Off'}
          </button>
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      width: 300,
      sortable: false,

      renderCell: (params, row) => (
        <div style={{
          display: "flex", flexDirection: "row", gap: "4px", alignItems: "center",
          height: "100%"
        }}>

          {(params.row.otp_verified === 0 && params.row.number !== '' && params.row.number !== undefined && params.row.number !== null) && (<button
            onClick={() => handleVerifyPhone(params.row)}
            style={{
              backgroundColor: "green",
              color: "white",
              border: "none",
              cursor: "pointer",
              borderRadius: "4px",
              fontSize: "10px",
              width: "100%",
              height: "24px",  // Increased height
              padding: "2px 5px", // Added padding
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Verify Phone
          </button>)}


          <button
            onClick={() => {
              // console.log("veiw is called")
              rememberReturn(params.row.id);
              navigate(NavigationRoutes.ADMIN_DASHBOARD + NavigationRoutes.USER_DETAILS.replace(":id", `:${params.row.id}`));
            }}
            style={{
              backgroundColor: "rgb(131 131 131)",
              color: "white",
              border: "none",
              cursor: "pointer",
              borderRadius: "4px",
              fontSize: "10px",
              width: "100%",
              height: "24px",  // Increased height
              padding: "2px 5px", // Added padding
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            View
          </button>
          <AddDealer editMode={true} tierList={tiers} dealerData={params.row} />
          <DealerBranches dealerData={params.row} />

          {/* <AddTierPopup tierList={tiers} userData={params.row} ></AddTierPopup> */}

          <button
            onClick={() => handleBlock(params.row)}
            style={{
              backgroundColor: "#F44336",
              color: "white",
              border: "none",
              cursor: "pointer",
              borderRadius: "4px",
              fontSize: "10px",
              width: "100%",
              height: "24px",  // Increased height
              padding: "2px 5px", // Added padding
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {params.row.status === 1 ? "Block" : "Unblock"}
          </button>

          <button
            onClick={() => handleFlag(params.row)}
            style={{
              backgroundColor: params.row.is_flagged === 1 ? "#ff9800" : "#607d8b",
              color: "white",
              border: "none",
              cursor: "pointer",
              borderRadius: "4px",
              fontSize: "10px",
              width: "100%",
              height: "24px",
              padding: "2px 5px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {params.row.is_flagged === 1 ? "Unflag" : "Flag"}
          </button>

          {/* Removing a duplicate dealer. The dialog asks the server what is
              attached first, and offers to move it onto the account being kept
              rather than refusing because the adverts are live. */}
          <button
            onClick={() => confirmDeleteOrMerge(params.row, reloadDealers)}
            style={{
              backgroundColor: "#F44336",
              color: "white",
              border: "none",
              cursor: "pointer",
              borderRadius: "4px",
              fontSize: "10px",
              width: "100%",
              height: "24px",
              padding: "2px 5px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Delete
          </button>

          {(params.row.email_verified === 0) && (<button
            onClick={() => handleVerifyEmail(params.row)}
            style={{
              backgroundColor: "green",
              color: "white",
              border: "none",
              cursor: "pointer",
              borderRadius: "4px",
              fontSize: "10px",
              width: "100%",
              height: "24px",  // Increased height
              padding: "2px 5px", // Added padding
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Verify Email
          </button>)}




        </div>

      ),
    },
  ];

  const handleBlock = (row) => {
    console.log("Deleting User ID:", row);
    Swal.fire({
      title: `${row.status === 1 ? "Block" : "Unblock"}`,
      html:
        "<hr>" +
        "Are you sure?" +
        "<br>" +
        `You want to  ${row.status === 1 ? "block" : "unblock"
        } ${row.name}`,
      showCancelButton: true,
      cancelButtonText: "Cancel",
      confirmButtonText: `${row.status === 1 ? "Block" : "Unblock"}`,
      showCloseButton: true,
      cancelButtonColor: "gray",
      confirmButtonColor: "#4f83df",
      reverseButtons: true
    }).then(async (result) => {
      if (result.isConfirmed) {
        dispatch(dealerListingUpdateUserStatus({ userId: row.id, status: row.status === 1 ? 0 : 1 }));
        var response = await patchData(`${ApiURLs.GET_USERS}/${row.id}`);
        if (response.status !== 1) {

          dispatch(dealerListingUpdateUserStatus({ userId: row.id, status: row.status === 1 ? 0 : 1 }));
        }
      }
    });

    // Add your delete logic here
  };




  const handleFlag = (row) => {
    const isFlagged = row.is_flagged === 1;
    Swal.fire({
      title: isFlagged ? "Remove Flag" : "Flag as Suspicious",
      html: `Are you sure you want to ${isFlagged ? "remove the flag from" : "flag"} <b>${row.business_name || row.name}</b>?`,
      showCancelButton: true,
      cancelButtonText: "Cancel",
      confirmButtonText: isFlagged ? "Remove Flag" : "Flag",
      confirmButtonColor: isFlagged ? "#607d8b" : "#ff9800",
      cancelButtonColor: "gray",
      reverseButtons: true
    }).then(async (result) => {
      if (result.isConfirmed) {
        const newFlag = isFlagged ? 0 : 1;
        await patchData(`${ApiURLs.USER_UPDATE}/${row.id}`, { is_flagged: newFlag });
        const sortField = sortModel[0]?.field || "created_at";
        const sortOrder = sortModel[0]?.sort || "desc";
        dispatch(dealerListingRequest(sortField, sortOrder, page + 1, pageSize, searchTerm));
      }
    });
  };

  const handleVerifyPhone = (row) => {
    // console.log("Deleting User ID:", row);
    Swal.fire({
      // title: `${"Verify Phone Confirmation"}`,
      html:

        "Are you sure?" +
        "<br>" +
        `You want to verify phone number  <b>+${row.flag} ${row.number}</b> of <b>${row.name}</b>`,
      showCancelButton: true,
      cancelButtonText: "Cancel",
      confirmButtonText: `Yes, Verify`,
      showCloseButton: true,
      cancelButtonColor: "gray",
      confirmButtonColor: "#4f83df",
      reverseButtons: true
    }).then(async (result) => {
      if (result.isConfirmed) {
        dispatch(dealerPhone({ userId: row.id, otp_verified: 1 }));
        var response = await patchData(`${ApiURLs.USER_UPDATE}/${row.id}`, { otp_verified: 1 });
        if (response.status !== 1) {

          dispatch(dealerPhone({ userId: row.id, otp_verified: 1 }));
        }
      }
    });

    // Add your delete logic here
  };




  React.useEffect(() => {


    if (!fetchedRef.current) {
      fetchedRef.current = true; // mark as fetched

      const fetchCategories = async () => {
        try {
          const result = await dispatch(getTierListing());
          setTiers(result);
          console.log("Fetched tiers:", result);
        } catch (error) {
          console.error("Error fetching tiers:", error);
        }
      };

      fetchCategories();
    }


    const sortField = sortModel[0]?.field || "created_at";
    const sortOrder = sortModel[0]?.sort || "desc";
    dispatch(dealerListingRequest(sortField, sortOrder, page + 1, pageSize, searchTerm, sector));
    // Save the view every time it changes, so Back returns to it.
    try {
      sessionStorage.setItem(
        VIEW_KEY,
        JSON.stringify({ page, pageSize, sortModel, searchTerm, sector })
      );
    } catch (e) {
      // A browser that refuses storage just loses the memory. Nothing breaks.
    }
  }, [sortModel, page, pageSize, searchTerm, sector, dispatch]);

  const rowsHere = dealerListing.dealerListing || [];
  React.useEffect(() => {
    if (dealerListing.loading || !rowsHere.length) return undefined;
    let saved = null;
    try { saved = JSON.parse(sessionStorage.getItem(RETURN_KEY) || 'null'); } catch (e) { saved = null; }
    if (!saved || !rowsHere.some((r) => String(r.id) === String(saved.id))) return undefined;
    try { sessionStorage.removeItem(RETURN_KEY); } catch (e) { /* ignore */ }
    setReturnId(saved.id);
    let userMoved = false;
    const stop = () => { userMoved = true; };
    const el = paperRef.current;
    ['wheel', 'touchstart', 'keydown', 'mousedown'].forEach((ev) => window.addEventListener(ev, stop, { passive: true, once: true }));
    let first = true;
    const apply = () => {
      if (userMoved) return;
      if (first) {
        first = false;
        if (el) el.scrollTop = saved.paper || 0;
        const g = gridScroller();
        if (g) g.scrollTop = saved.grid || 0;
        if (saved.win) window.scrollTo({ top: saved.win, behavior: 'instant' });
      }
      // The same scroll numbers are not the same place: the cards above load at
      // a different height. Move until the dealer's row is where it was.
      const row = el && el.querySelector(`.MuiDataGrid-row[data-id="${saved.id}"]`);
      if (row && saved.rowTop != null) {
        const delta = row.getBoundingClientRect().top - saved.rowTop;
        if (Math.abs(delta) > 2) {
          // instant: this page sets scroll-behavior: smooth, and a smooth
          // scroll has not moved yet when the next correction measures it
          const before = window.scrollY;
          window.scrollBy({ top: delta, behavior: 'instant' });
          if (window.scrollY === before && el) el.scrollTop += delta;
        }
      } else if (row) {
        row.scrollIntoView({ block: 'center', behavior: 'instant' });
      }
    };
    // Keep the row in place while the rest of the page settles (the summary
    // cards above can arrive a few seconds later), then let go.
    apply();
    const tick = setInterval(apply, 150);
    const done = setTimeout(() => clearInterval(tick), 8000);
    const clear = setTimeout(() => setReturnId(null), 5000);
    return () => {
      clearInterval(tick);
      clearTimeout(done);
      clearTimeout(clear);
      ['wheel', 'touchstart', 'keydown', 'mousedown'].forEach((ev) => window.removeEventListener(ev, stop));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealerListing.loading, rowsHere]);

  return (

    <Paper ref={paperRef} sx={{ height: "95%", width: '100%', overflow: 'auto' }}>

      <DealerDataPanel
        activeSector={sector}
        onPickSector={(s) => { setPage(0); setSector(s); }}
        refreshKey={dataVersion}
      />

      <div style={{
        padding: "10px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
      }}>
        <input
          type="text"
          placeholder="Search by Account No, Dealer Name or Email"
          value={searchTerm}
          onChange={(e) => {
            setPage(0);
            setSearchTerm(e.target.value);
          }}
          style={{
            padding: "6px 10px",
            fontSize: "14px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            width: "50%"
          }}
        />
        <AddDealer tierList={tiers} />
      </div>

      <DataGrid
        rows={dealerListing.dealerListing || []}
        columns={dealerListingHeading || []}
        rowCount={dealerListing.totalDealers || 0} // ✅ Total count from Redux
        paginationMode="server" // ✅ Enable manual pagination
        sortingMode="server"
        page={page}
        pageSize={pageSize}
        sortModel={sortModel}
        onSortModelChange={(model) => setSortModel(model)}
        pageSizeOptions={[10, 25, 50]}
        loading={dealerListing.loading}
        checkboxSelection={false}
        sx={{ border: 0, '& .row-came-back': { backgroundColor: '#fff4cc', transition: 'background-color 1s' } }}
        getRowClassName={(p) => (returnId != null && String(p.id) === String(returnId) ? 'row-came-back' : '')}
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={(model) => {
          setPage(model.page);
          setPageSize(model.pageSize);
        }}


      />
    </Paper>
  );
}

//Getting the state from the store
const mapStateToProps = (state) => {
  return {
    dealerListing: state.dealerListing,
  };
};

//passing the userData in fetchUsers function and also dispatch method
const mapDispatchToProps = (dispatch) => {
  return {
    dealerLisiting: (data, navigate) => dispatch(dealerListingRequest(data, navigate)),
  };
};

//Connecting the component to our store
export default connect(mapStateToProps, mapDispatchToProps)(DealerLisitingTable);
